import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CurrencyInput } from "@/components/ui/currency-input";
import type { SubscriptionSettingsView } from "@/types";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { subscriptionConfigError, type SubscriptionConfigPatch } from "./config-validation";

/** Emptied number input stays NaN in the draft so submit can name the field. */
function numberValue(n: number): number | "" {
  return Number.isFinite(n) ? n : "";
}

/** "" -> NaN (empty), "1.5" stays 1.5 so the integer rule can reject it instead of truncating. */
function parseNumberInput(raw: string): number {
  return raw.trim() === "" ? Number.NaN : Number(raw);
}

export function ConfigForm() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["admin", "subscription-settings"],
    queryFn: async () => {
      const res = await fetch("/api/admin/subscription-settings");
      await throwIfNotOk(res, "Greška pri učitavanju postavki pretplate.");
      return res.json() as Promise<SubscriptionSettingsView>;
    },
  });

  const [draft, setDraft] = useState<SubscriptionSettingsView | null>(null);
  useEffect(() => {
    if (data && !draft) setDraft(structuredClone(data));
  }, [data, draft]);

  const saveMutation = useMutation({
    mutationFn: async (patch: SubscriptionConfigPatch) => {
      const invalid = subscriptionConfigError(patch);
      if (invalid) throw new Error(invalid);
      const res = await fetch("/api/admin/subscription-settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(patch),
      });
      await throwIfNotOk(res, "Greška pri spremanju postavki pretplate.");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "subscription-settings"] });
      toast.success("Postavke pretplate spremljene");
    },
  });

  if (!draft) return <div className="text-sm text-stone-500">Učitavanje…</div>;

  return (
    <div className="space-y-6">
      <section className="rounded-xl border border-stone-200 bg-white p-6">
        <h3 className="text-base font-semibold mb-1">Cijene pretplate</h3>
        <p className="text-sm text-stone-500 mb-5">
          Promjena cijene primjenjuje se samo na nove pretplate.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Polugodišnja">
            <CurrencyInput
              suffix="BAM"
              value={Number.isFinite(draft.priceSemiannual) ? draft.priceSemiannual : null}
              onChange={(v) => setDraft({ ...draft, priceSemiannual: v ?? Number.NaN })}
            />
          </Field>
          <Field label="Godišnja">
            <CurrencyInput
              suffix="BAM"
              value={Number.isFinite(draft.priceAnnual) ? draft.priceAnnual : null}
              onChange={(v) => setDraft({ ...draft, priceAnnual: v ?? Number.NaN })}
            />
          </Field>
          <Field label="PDV stopa (npr. 0.17 = 17%)">
            <Input
              type="number"
              step="0.01"
              min={0}
              max={1}
              value={numberValue(draft.vatRate)}
              onChange={(e) => setDraft({ ...draft, vatRate: parseNumberInput(e.target.value) })}
            />
          </Field>
          <Field label="Cijena uključuje PDV?">
            <Label className="mt-2 font-normal">
              <Checkbox
                checked={draft.vatIncluded}
                onCheckedChange={(c) => setDraft({ ...draft, vatIncluded: c === true })}
              />
              Da
            </Label>
          </Field>
          <Field label="Trajanje payment link-a (sati)">
            <Input
              type="number"
              min={1}
              max={720}
              step={1}
              value={numberValue(draft.paymentLinkTtlHours)}
              onChange={(e) => setDraft({ ...draft, paymentLinkTtlHours: parseNumberInput(e.target.value) })}
            />
          </Field>
          <Field label="Reminder prije isteka (dani)">
            <Input
              type="number"
              min={0}
              max={90}
              step={1}
              value={numberValue(draft.renewalReminderDays)}
              onChange={(e) => setDraft({ ...draft, renewalReminderDays: parseNumberInput(e.target.value) })}
            />
          </Field>
          <Field label="Max podsjetnika po pokretanju">
            <Input
              type="number"
              min={0}
              max={1000}
              step={1}
              value={numberValue(draft.maxRemindersPerRun)}
              onChange={(e) => setDraft({ ...draft, maxRemindersPerRun: parseNumberInput(e.target.value) })}
            />
            <p className="text-xs text-stone-500">
              Gornja granica emailova po jednom pokretanju. Prednost imaju
              terapeuti kojima pretplata najskorije ističe. Ostatak ide u
              sljedeće pokretanje. 0 znači bez ograničenja.
            </p>
          </Field>
        </div>
      </section>

      <section className="rounded-xl border border-stone-200 bg-white p-6">
        <h3 className="text-base font-semibold mb-1">Podaci izdavaoca</h3>
        <p className="text-sm text-stone-500 mb-5">
          Pojavljuju se na PDF računima i bank transfer info-u. Snapshot se snima u trenutku plaćanja.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Naziv firme">
            <Input
              maxLength={200}
              value={draft.issuer.businessName}
              onChange={(e) => setDraft({ ...draft, issuer: { ...draft.issuer, businessName: e.target.value } })}
            />
          </Field>
          <Field label="Adresa">
            <Input
              maxLength={400}
              value={draft.issuer.businessAddress}
              onChange={(e) => setDraft({ ...draft, issuer: { ...draft.issuer, businessAddress: e.target.value } })}
            />
          </Field>
          <Field label="JIB">
            <Input
              maxLength={20}
              value={draft.issuer.taxId}
              onChange={(e) => setDraft({ ...draft, issuer: { ...draft.issuer, taxId: e.target.value } })}
            />
          </Field>
          <Field label="PDV broj">
            <Input
              maxLength={20}
              value={draft.issuer.vatId}
              onChange={(e) => setDraft({ ...draft, issuer: { ...draft.issuer, vatId: e.target.value } })}
            />
          </Field>
          <Field label="IBAN">
            <Input
              maxLength={40}
              value={draft.issuer.iban}
              onChange={(e) => setDraft({ ...draft, issuer: { ...draft.issuer, iban: e.target.value } })}
            />
          </Field>
          <Field label="Naziv banke">
            <Input
              maxLength={200}
              value={draft.issuer.bankName}
              onChange={(e) => setDraft({ ...draft, issuer: { ...draft.issuer, bankName: e.target.value } })}
            />
          </Field>
        </div>
      </section>

      <div className="flex justify-end">
        <Button
          onClick={() =>
            saveMutation.mutate({
              priceSemiannual: draft.priceSemiannual,
              priceAnnual: draft.priceAnnual,
              vatRate: draft.vatRate,
              vatIncluded: draft.vatIncluded,
              paymentLinkTtlHours: draft.paymentLinkTtlHours,
              renewalReminderDays: draft.renewalReminderDays,
              maxRemindersPerRun: draft.maxRemindersPerRun,
              issuer: draft.issuer,
            })
          }
          disabled={saveMutation.isPending}
        >
          {saveMutation.isPending ? "Snimam…" : "Sačuvaj"}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
